import React, { Dispatch, SetStateAction, useCallback, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  Checkbox,
  Collapse,
  FormControl,
  FormControlLabel,
  FormLabel,
  Grid,
  IconButton,
  MenuItem,
  Radio,
  RadioGroup,
  TextField,
} from '@material-ui/core';
import WarningIcon from '@material-ui/icons/Warning';
import Alert from '@material-ui/lab/Alert';
import * as R from 'ramda';
import { useToggle } from 'react-use';
import { Settings } from '@material-ui/icons';
import { Trans, useTranslation } from 'react-i18next';

import { getUseCreateCustomerBeta } from 'reducers/configs/settings';
import { saveTempSetting } from 'actions/configs';

import {
  CUSTOMER_TYPES,
  CustomerType,
  CustomerFormConfiguration as CustomerPOSConfig,
  FormFieldConfiguration as FormFieldConfig,
  FormFieldName,
} from '../types';

import FormFieldConfiguration from './FormFieldConfiguration';

interface Props {
  config: CustomerPOSConfig;
  updateConfig: Dispatch<SetStateAction<CustomerPOSConfig>>;
}

const CustomerFormConfiguration = ({ config, updateConfig }: Props) => {
  const { t } = useTranslation('customerFormBeta');
  const dispatch = useDispatch();
  const [showConfig, toggleConfig] = useToggle(false);

  const isConfigurableCustomerFromEnabled = useSelector(
    getUseCreateCustomerBeta,
  );
  const toggleCustomerFormBeta = () => {
    dispatch(
      saveTempSetting(
        'pos_brazil_use_customer_creation_beta',
        !isConfigurableCustomerFromEnabled,
      ),
    );
  };

  const [customerType, setCustomerType] = useState<CustomerType>(
    CUSTOMER_TYPES.PERSON,
  );

  const updateConfigPerField = useCallback(
    (field: FormFieldName, config: FormFieldConfig) => {
      updateConfig(R.assocPath(['formFields', customerType, field], config));
    },
    [customerType, updateConfig],
  );

  const updateAllowedCustomerType = e => {
    updateConfig(R.assoc('allowedCustomerTypes', e.target.value));
  };

  return (
    <Grid item xs={12} container spacing={1}>
      <Grid container>
        <Grid item>
          <FormControl component="fieldset">
            <FormControlLabel
              control={
                <Checkbox
                  checked={isConfigurableCustomerFromEnabled}
                  onChange={toggleCustomerFormBeta}
                />
              }
              label={t('config.enable')}
            />
          </FormControl>
        </Grid>
        <Grid item xs />
        <Grid item>
          <IconButton onClick={toggleConfig}>
            <Settings />
          </IconButton>
        </Grid>
      </Grid>
      <Collapse in={showConfig}>
        <Grid item xs={12}>
          <h2>{t('title')}</h2>
        </Grid>
        <Trans
          ns="customerFormBeta"
          i18nKey="description"
          components={(function createComponents() {
            function P({ children }: any) {
              return (
                <Grid item xs={12}>
                  <p>{children}</p>
                </Grid>
              );
            }
            function Warning({ children }: any) {
              return (
                <Grid item xs={12}>
                  <Alert color="warning">
                    <WarningIcon />
                    {children}
                  </Alert>
                </Grid>
              );
            }
            return { p: <P />, warning: <Warning /> };
          })()}
        />

        <Grid item xs={12}>
          <FormControl component="fieldset">
            <FormLabel component="legend">{t('config.allowTypes')}</FormLabel>
            <RadioGroup
              aria-label="allowed customer type"
              defaultValue=""
              value={config.allowedCustomerTypes}
              onChange={updateAllowedCustomerType}
            >
              <FormControlLabel
                value={CUSTOMER_TYPES.PERSON}
                control={<Radio />}
                label={t('customerType.PERSON')}
              />
              <FormControlLabel
                value={CUSTOMER_TYPES.COMPANY}
                control={<Radio />}
                label={t('customerType.COMPANY')}
              />
              <FormControlLabel
                value=""
                control={<Radio />}
                label={t('customerType.both')}
              />
            </RadioGroup>
          </FormControl>
        </Grid>
        <Grid item xs={12}>
          <p>{t('config.selectedType')}</p>
        </Grid>
        <Grid item xs={12}>
          <TextField
            select
            label={t('config.selectedTypeLabel')}
            variant="outlined"
            fullWidth
            onChange={e => setCustomerType(e.target.value as CustomerType)}
            value={customerType}
          >
            <MenuItem value={CUSTOMER_TYPES.PERSON}>
              {t('customerType.PERSON')}
            </MenuItem>
            <MenuItem value={CUSTOMER_TYPES.COMPANY}>
              {t('customerType.COMPANY')}
            </MenuItem>
          </TextField>
        </Grid>
        <Grid item xs={12}>
          <FormFieldConfiguration
            config={config}
            customerType={customerType}
            updateConfigPerField={updateConfigPerField}
          />
        </Grid>
      </Collapse>
    </Grid>
  );
};

export default CustomerFormConfiguration;
